import { FunctionComponent } from 'react'
import { Success, AlertFilled, WarningFilled, ShieldFilled } from 'components/atoms/icons'
import invert from 'lodash/invert'
import { SAFETY_RATING, TCarrierStatus } from 'types/carrier'
import { ICarrierAssignmentBounceReason } from 'types/graphqlTypes'

export const carrierStatuses: Record<TCarrierStatus, string> = {
  activated: 'Active',
  new: 'Fresh',
  pending: 'Pending',
  application_submitted: 'App. Submitted',
  in_review: 'In Review',
  expired_insurance: 'Expired Ins.',
  out_of_service: 'Out of Service',
  conditional: 'Conditional',
  cancelled: 'Cancelled',
  other: 'Other',
  probation: 'Probation',
  do_not_use: 'Cannot Use',
  inactive: 'Inactive',
}

export const carrierStatusOptions = Object.entries(carrierStatuses).map(([k, value]) => ({
  value: k,
  label: value,
}))

export const CARRIER_STATUS_ICON_MAP = {
  'Expired Ins.': { Icon: WarningFilled, color: 'warning' },
  Inactive: { Icon: AlertFilled, color: 'error' },
  'Out of Service': { Icon: AlertFilled, color: 'error' },
  Conditional: { Icon: AlertFilled, color: 'error' },
  Cancelled: { Icon: AlertFilled, color: 'error' },
  'Cannot Use': { Icon: AlertFilled, color: 'error' },
  Probation: { Icon: AlertFilled, color: 'error' },
  Pending: { Icon: WarningFilled, color: 'warning' },
  'App. Submitted': { Icon: WarningFilled, color: 'warning' },
  'In Review': { Icon: WarningFilled, color: 'warning' },
  Active: { Icon: Success, color: 'success' },
  Fresh: { Icon: WarningFilled, color: 'warning' },
}

export const CARRIER_STATUSES = Object.entries(carrierStatuses).map(
  ([key, name]) =>
    ({ name, key } as { name: keyof typeof CARRIER_STATUS_ICON_MAP; key: TCarrierStatus })
)
interface IApptCommonProps {
  tooltip?: string
  className?: string
}

export const FMCSA_STATUS_ICON_MAP: Record<
  SAFETY_RATING,
  {
    Icon: FunctionComponent<IApptCommonProps>
    color: string
    className: string
  }
> = {
  CONDITIONAL: { Icon: ShieldFilled, color: 'text-error', className: 'icon-error' },
  SATISFACTORY: { Icon: ShieldFilled, color: 'text-success', className: 'icon-success' },
  UNSATISFACTORY: { Icon: ShieldFilled, color: 'text-error', className: 'icon-error' },
  'NOT RATED': { Icon: ShieldFilled, color: 'text-secondary', className: 'icon-secondary' },
}

export const CARRIER_KEY_NAME_LOOKUP = invert(carrierStatuses)

export const MANAGER_ROLES = [
  {
    id: 'owner',
    name: 'Primary Manager',
  },
  {
    id: 'secondary',
    name: 'Secondary Manager',
  },
  {
    id: 'any',
    name: 'Primary or Secondary',
  },
]

export const COMMON_REJECTED_REASONS = [
  'Equipment',
  'Shipper',
  'Appointment time',
  'Commodity',
  'Rate',
]

export const OFFER_REJECTED_REASONS = [
  ...COMMON_REJECTED_REASONS,
  'No capacity - offering late',
  'No capacity - offering early',
  'No capacity - day of the week',
  'No capacity - fleet size',
  'No capacity - on a Transfix load',
  'No capacity - driver sick',
  'No capacity - mechanical issues',
  'No capacity - other',
]

export const CONFLICT_CARRIER_APPROVED_REASONS = [
  'Has capacity and others do not',
  'Keeping multiple carriers engaged',
  'Primary carrier on lane, other secondary',
  'Best rate on this lane',
]

export const CONFLICT_CARRIERS_REJECTED_REASONS = [
  ...COMMON_REJECTED_REASONS,
  'None have capacity - offering late',
  'None have capacity - offering early',
  'None have capacity - day of the week',
  'None have capacity - fleet size',
  'None have capacity - on Transfix loads',
  'None have capacity - drivers sick',
  'None have capacity - mechanical issues',
  'None have capacity - other',
]

export const LOCATION_KEYS = ['location', 'contactPhone']

export const STEP = 25
export const RADII = new Array(9).fill(STEP).map((v, i) => v * i)
export const DEFAULT_RADIUS = STEP
export const RADIUS_RANGES = RADII.slice(1).map((max, id) => ({
  id,
  min: max - STEP,
  max,
}))

export const DEFAULT_CARRIER_SCORE = { bounceRate: 0, otp30: '0', otd30: '0' }

export const CARRIER_COMPLIANCE_STATUSES = {
  offer: 'Ready to Offer',
  inReview: 'In Compliance Review',
  needsReview: 'Needs Compliance Review',
}

export const REMOVAL_TYPE_OPTIONS: {
  id: string
  label: string
  value: keyof typeof ICarrierAssignmentBounceReason
}[] = [
  {
    id: 'carrier-poor-planning',
    label: 'Carrier - Carrier poor planning',
    value: 'CARRIER_CARRIER_POOR_PLANNING',
  },
  {
    id: 'carrier-no-capacity',
    label: 'Carrier - No capacity',
    value: 'CARRIER_NO_CAPACITY',
  },
  {
    id: 'carrier-no-otp',
    label: 'Carrier - No OTP',
    value: 'CARRIER_NO_OTP',
  },
  {
    id: 'carrier-breakdown-accident',
    label: 'Carrier - Breakdown / accident',
    value: 'CARRIER_BREAKDOWN_ACCIDENT',
  },
  {
    id: 'carrier-no-docs',
    label: 'Carrier - No docs',
    value: 'CARRIER_NO_DOCS',
  },
  {
    id: 'carrier-no-track-no-response',
    label: 'Carrier - No track / no response',
    value: 'CARRIER_NO_TRACK_NO_RESPONSE',
  },
  {
    id: 'carrier-driver-issue',
    label: 'Carrier - Driver issue',
    value: 'CARRIER_DRIVER_ISSUE',
  },
  {
    id: 'carrier-rejected-trailer',
    label: 'Carrier - Rejected trailer',
    value: 'CARRIER_REJECTED_TRAILER',
  },
  {
    id: 'carrier-load-built-incorrectly',
    label: 'Transfix - Load built incorrectly',
    value: 'TRANSFIX_LOAD_BUILT_INCORRECTLY',
  },
  {
    id: 'carrier-miscommunication-with-driver',
    label: 'Transfix - Miscommunication with driver',
    value: 'TRANSFIX_MISCOMMUNICATION_WITH_DRIVER',
  },
  {
    id: 'carrier-changhed-load-number',
    label: 'Transfix - Changed load #',
    value: 'TRANSFIX_CHANGED_LOAD_NUM',
  },
  {
    id: 'carrier-changes-to-shipment',
    label: 'Shipper - Changes to shipment',
    value: 'SHIPPER_CHANGES_TO_SHIPMENT',
  },
  {
    id: 'carrier-load-not-ready',
    label: 'Shipper - Load not ready',
    value: 'SHIPPER_LOAD_NOT_READY',
  },
  {
    id: 'carrier-double-tendered',
    label: 'Shipper - Double tendered',
    value: 'SHIPPER_DOUBLE_TENDERED',
  },
  {
    id: 'carrier-load-cancelled',
    label: 'Shipper - Load cancelled',
    value: 'SHIPPER_LOAD_CANCELLED',
  },
  {
    id: 'carrier-extreme-weather',
    label: 'No fault - Extreme weather',
    value: 'NO_FAULT_EXTREME_WEATHER',
  },
  {
    id: 'carrier-road-closure',
    label: 'No fault - Road closure',
    value: 'NO_FAULT_ROAD_CLOSURE',
  },
  {
    id: 'uncancele-untonu',
    label: 'Uncancel/UnTONU',
    value: 'UNCANCEL_UNTONU',
  },
  {
    id: 'acceptable-switch',
    label: 'Acceptable Switch',
    value: 'ACCEPTABLE_SWITCH',
  },
  {
    id: 'carrier-change',
    label: 'Carrier change',
    value: 'CARRIER_CHANGE',
  },
  {
    id: 'carrier-negligence',
    label: 'Carrier negligence',
    value: 'CARRIER_NEGLIGENCE',
  },
  {
    id: 'customer-negligence',
    label: 'Customer negligence',
    value: 'CUSTOMER_NEGLIGENCE',
  },
  {
    id: 'no-fault',
    label: 'No fault',
    value: 'NO_FAULT',
  },
  {
    id: 'no-reason-provided',
    label: 'no reason provided',
    value: 'NO_REASON_PROVIDED',
  },
  {
    id: 'tonu',
    label: 'TONU',
    value: 'TONU',
  },
  {
    id: 'transfix-negligence',
    label: 'Transfix negligence',
    value: 'TRANSFIX_NEGLIGENCE',
  },
  {
    id: 'declined-manual-offer-via-edi-990',
    label: 'declined manual offer via EDI 990',
    value: 'DECLINED_MANUAL_OFFER_VIA_EDI_990',
  },
  {
    id: 'declined-offer-via-fms',
    label: 'declined offer via FMS',
    value: 'DECLINED_OFFER_VIA_FMS',
  },
]
